import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 24: DHARA
 * Style: Modern Editorial Storytelling
 * Inspired by: Raw Mango, Sabyasachi, Anavila
 * Best for: Story-driven Indian fashion, artisan craft brands, luxury ethnic wear
 * Clean white, forest green accent, editorial magazine-style homepage
 */
const Template24 = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [hoveredCollection, setHoveredCollection] = useState(null);
    const [hoveredJournal, setHoveredJournal] = useState(null);
    const videoRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 80);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Editorial Collection Blocks
    const collections = [
        {
            name: 'MONSOON 2026',
            tagline: 'Where rain meets ritual',
            description: 'A meditation on the monsoon — fluid silhouettes in rain-washed indigo and moss green, handwoven on pit looms by master artisans of Maheshwar.',
            image: 'https://images.pexels.com/photos/8387170/pexels-photo-8387170.jpeg?auto=compress&cs=tinysrgb&w=1920',
        },
        {
            name: 'THE EVENING EDIT',
            tagline: 'For the hours between dusk and dawn',
            description: 'Silk chanderi and raw silk pieces designed for intimate gatherings — understated luxury that speaks through texture, not volume.',
            image: 'https://images.pexels.com/photos/14767148/pexels-photo-14767148.jpeg?auto=compress&cs=tinysrgb&w=1920',
        },
        {
            name: 'HERITAGE WEAVES',
            tagline: 'Seven generations of thread',
            description: 'Our collaboration with Banarasi weavers — each piece carrying forward centuries of craft, reimagined for the modern wardrobe.',
            image: 'https://images.pexels.com/photos/11810731/pexels-photo-11810731.jpeg?auto=compress&cs=tinysrgb&w=1920',
        },
    ];

    // Minimal Product Peek — 4 products only
    const products = [
        {
            id: 1,
            name: 'Silk Chanderi Drape',
            price: '\u20B914,500',
            image: 'https://images.pexels.com/photos/5595710/pexels-photo-5595710.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            id: 2,
            name: 'The Monsoon Kurta',
            price: '\u20B97,800',
            image: 'https://images.pexels.com/photos/8770930/pexels-photo-8770930.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            id: 3,
            name: 'Heritage Banarasi Sari',
            price: '\u20B922,000',
            image: 'https://images.pexels.com/photos/28867973/pexels-photo-28867973.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            id: 4,
            name: 'Evening Silk Blouse',
            price: '\u20B96,500',
            image: 'https://images.pexels.com/photos/7850255/pexels-photo-7850255.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
    ];

    // Journal Articles
    const journalArticles = [
        {
            id: 1,
            title: 'The Quiet Art of Handloom',
            excerpt: 'Inside the homes of Maheshwar weavers, where every thread carries a prayer and every sari takes three weeks to complete.',
            image: 'https://images.pexels.com/photos/3328127/pexels-photo-3328127.jpeg?auto=compress&cs=tinysrgb&w=1200',
            date: 'March 2026',
        },
        {
            id: 2,
            title: 'Dressing for the Monsoon',
            excerpt: 'A seasonal guide to fabrics that breathe, drape, and move with grace through the wettest months of the Indian calendar.',
            image: 'https://images.pexels.com/photos/17503286/pexels-photo-17503286.jpeg?auto=compress&cs=tinysrgb&w=1200',
            date: 'February 2026',
        },
    ];

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (email.trim()) {
            setSubscribed(true);
            setEmail('');
            setTimeout(() => setSubscribed(false), 4000);
        }
    };

    return (
        <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", backgroundColor: '#FFFFFF' }}>
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap');

                .dhara-hero-link {
                    color: #FFFFFF;
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 400;
                    letter-spacing: 2.5px;
                    text-transform: uppercase;
                    text-decoration: none;
                    border: none;
                    background: none;
                    cursor: pointer;
                    padding: 8px 0;
                    border-bottom: 1px solid rgba(255,255,255,0.4);
                    transition: all 0.3s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                .dhara-hero-link:hover {
                    border-bottom-color: #FFFFFF;
                }

                .dhara-collection-block {
                    position: relative;
                    width: 100%;
                    overflow: hidden;
                    cursor: pointer;
                }
                .dhara-collection-block img {
                    transition: transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                }
                .dhara-collection-block:hover img {
                    transform: scale(1.03);
                }

                .dhara-explore-link {
                    color: #00553A;
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 500;
                    letter-spacing: 2px;
                    text-transform: uppercase;
                    text-decoration: none;
                    border: none;
                    background: none;
                    cursor: pointer;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s ease;
                }
                .dhara-explore-link:hover {
                    color: #006B4A;
                    gap: 12px;
                }

                .dhara-product-img {
                    transition: transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                }

                .dhara-journal-card:hover .dhara-journal-img {
                    transform: scale(1.03);
                }

                .dhara-newsletter-input-main:focus {
                    border-color: #00553A !important;
                    outline: none;
                }

                @media (max-width: 768px) {
                    .dhara-split-grid { grid-template-columns: 1fr !important; }
                    .dhara-products-grid { grid-template-columns: 1fr 1fr !important; gap: 24px !important; }
                    .dhara-journal-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
                    .dhara-collection-content { padding: 40px 24px !important; }
                    .dhara-hero-title { font-size: 48px !important; }
                    .dhara-section-padding { padding-left: 24px !important; padding-right: 24px !important; }
                    .dhara-collection-name { font-size: 32px !important; }
                    .dhara-split-text { padding: 48px 24px !important; }
                    .dhara-newsletter-section { padding: 64px 24px !important; }
                }
                @media (max-width: 480px) {
                    .dhara-hero-title { font-size: 36px !important; }
                    .dhara-collection-name { font-size: 26px !important; }
                }
            `}</style>

            {/* ===== SECTION 1: VIDEO/IMAGE HERO — Full Viewport ===== */}
            <section
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100vh',
                    minHeight: '600px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#0A0A0A',
                }}
            >
                {/* Video Background */}
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster="https://images.pexels.com/photos/8387170/pexels-photo-8387170.jpeg?auto=compress&cs=tinysrgb&w=1920"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.55,
                    }}
                >
                    <source src="https://assets.mixkit.co/videos/805/805-720.mp4" type="video/mp4" />
                </video>

                {/* Subtle Gradient Overlay */}
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.35) 100%)',
                    }}
                />

                {/* Hero Content — Ultra Minimal */}
                <div
                    style={{
                        position: 'relative',
                        zIndex: 10,
                        textAlign: 'center',
                        padding: '0 24px',
                        maxWidth: '800px',
                    }}
                >
                    <p
                        style={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: '11px',
                            fontWeight: 400,
                            letterSpacing: '4px',
                            textTransform: 'uppercase',
                            color: 'rgba(255,255,255,0.7)',
                            marginBottom: '24px',
                        }}
                    >
                        Monsoon 2026 Collection
                    </p>
                    <h1
                        className="dhara-hero-title"
                        style={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                            fontSize: '72px',
                            fontWeight: 400,
                            color: '#FFFFFF',
                            letterSpacing: '6px',
                            textTransform: 'uppercase',
                            marginBottom: '24px',
                            lineHeight: 1.05,
                        }}
                    >
                        DHARA
                    </h1>
                    <p
                        style={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: '15px',
                            fontWeight: 300,
                            color: 'rgba(255,255,255,0.65)',
                            letterSpacing: '0.5px',
                            marginBottom: '48px',
                            lineHeight: 1.6,
                        }}
                    >
                        Stories woven in silk. Craft that flows.
                    </p>
                    <button
                        className="dhara-hero-link"
                        onClick={() => navigate('/template24/collections')}
                    >
                        View Collection
                        <ArrowRight size={14} strokeWidth={1.5} />
                    </button>
                </div>

                {/* Scroll Indicator */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: '40px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <div
                        style={{
                            width: '1px',
                            height: '40px',
                            background: 'linear-gradient(to bottom, rgba(255,255,255,0.5), transparent)',
                        }}
                    />
                </div>
            </section>

            {/* ===== SECTION 2: EDITORIAL COLLECTION BLOCKS ===== */}
            {collections.map((collection, index) => (
                <section
                    key={index}
                    className="dhara-collection-block"
                    onClick={() => navigate('/template24/collections')}
                    onMouseEnter={() => setHoveredCollection(index)}
                    onMouseLeave={() => setHoveredCollection(null)}
                    style={{
                        height: index === 1 ? '85vh' : '90vh',
                        minHeight: '500px',
                        display: 'flex',
                        alignItems: index === 1 ? 'flex-end' : 'center',
                        justifyContent: index % 2 === 0 ? 'flex-start' : 'flex-end',
                    }}
                >
                    {/* Full-Bleed Image */}
                    <img
                        src={collection.image}
                        alt={collection.name}
                        loading="lazy"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />

                    {/* Dark Scrim */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: index === 1
                                ? 'linear-gradient(0deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)'
                                : 'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 50%, transparent 100%)',
                        }}
                    />

                    {/* Content Overlay */}
                    <div
                        className="dhara-collection-content"
                        style={{
                            position: 'relative',
                            zIndex: 10,
                            padding: index === 1 ? '80px 60px' : '60px',
                            maxWidth: '560px',
                        }}
                    >
                        <p
                            style={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '11px',
                                fontWeight: 400,
                                letterSpacing: '3px',
                                textTransform: 'uppercase',
                                color: 'rgba(255,255,255,0.6)',
                                marginBottom: '16px',
                            }}
                        >
                            {collection.tagline}
                        </p>
                        <h2
                            className="dhara-collection-name"
                            style={{
                                fontFamily: "'Playfair Display', Georgia, serif",
                                fontSize: '42px',
                                fontWeight: 400,
                                color: '#FFFFFF',
                                letterSpacing: '4px',
                                marginBottom: '20px',
                                lineHeight: 1.1,
                            }}
                        >
                            {collection.name}
                        </h2>
                        <p
                            style={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '14px',
                                fontWeight: 300,
                                color: 'rgba(255,255,255,0.7)',
                                lineHeight: 1.8,
                                marginBottom: '32px',
                                maxWidth: '420px',
                            }}
                        >
                            {collection.description}
                        </p>
                        <button
                            className="dhara-hero-link"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate('/template24/collections');
                            }}
                        >
                            Explore
                            <ArrowRight size={14} strokeWidth={1.5} />
                        </button>
                    </div>
                </section>
            ))}

            {/* ===== SECTION 3: SPLIT CONTENT — Story Block ===== */}
            <section
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    minHeight: '80vh',
                    backgroundColor: '#FFFFFF',
                }}
                className="dhara-split-grid"
            >
                {/* Image Left */}
                <div
                    style={{
                        overflow: 'hidden',
                        minHeight: '500px',
                    }}
                >
                    <img
                        src="https://images.pexels.com/photos/3328127/pexels-photo-3328127.jpeg?auto=compress&cs=tinysrgb&w=1200"
                        alt="Artisan craftsmanship"
                        loading="lazy"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                </div>

                {/* Text Right */}
                <div
                    className="dhara-split-text"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding: '80px 80px 80px 64px',
                        maxWidth: '600px',
                    }}
                >
                    <p
                        style={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: '11px',
                            fontWeight: 500,
                            letterSpacing: '3px',
                            textTransform: 'uppercase',
                            color: '#00553A',
                            marginBottom: '24px',
                        }}
                    >
                        Our Craft
                    </p>
                    <h2
                        style={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                            fontSize: '36px',
                            fontWeight: 400,
                            color: '#1A1A1A',
                            lineHeight: 1.2,
                            marginBottom: '28px',
                            letterSpacing: '-0.5px',
                        }}
                    >
                        Seven Generations of Thread
                    </h2>
                    <p
                        style={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: '15px',
                            fontWeight: 300,
                            color: '#6B6B6B',
                            lineHeight: 1.9,
                            marginBottom: '32px',
                        }}
                    >
                        Every Dhara piece begins in the hands of master artisans — families who have woven for seven
                        generations across the banks of the Narmada. Our monsoon collection is born from this lineage:
                        pit-loom Maheshwari weaves, hand-block prints from Bagru, and Chanderi silk spun with stories.
                    </p>

                    {/* Pull Quote */}
                    <blockquote
                        style={{
                            borderLeft: '2px solid #00553A',
                            paddingLeft: '24px',
                            marginBottom: '40px',
                            marginLeft: 0,
                            marginRight: 0,
                        }}
                    >
                        <p
                            style={{
                                fontFamily: "'Playfair Display', Georgia, serif",
                                fontSize: '18px',
                                fontWeight: 400,
                                fontStyle: 'italic',
                                color: '#1A1A1A',
                                lineHeight: 1.6,
                            }}
                        >
                            "We don't design clothes. We listen to the loom and translate what it tells us."
                        </p>
                    </blockquote>

                    <button
                        className="dhara-explore-link"
                        onClick={() => navigate('/template24/about')}
                    >
                        Read Our Story
                        <ArrowRight size={14} strokeWidth={1.5} />
                    </button>
                </div>
            </section>

            {/* ===== SECTION 4: MINIMAL PRODUCT PEEK ===== */}
            <section
                className="dhara-section-padding"
                style={{
                    padding: '100px 60px 80px',
                    backgroundColor: '#FAFAF8',
                    maxWidth: '100%',
                }}
            >
                <div style={{ maxWidth: '1300px', margin: '0 auto' }}>
                    {/* Section Header */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            marginBottom: '56px',
                            flexWrap: 'wrap',
                            gap: '16px',
                        }}
                    >
                        <div>
                            <p
                                style={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    letterSpacing: '3px',
                                    textTransform: 'uppercase',
                                    color: '#00553A',
                                    marginBottom: '12px',
                                }}
                            >
                                Selected Pieces
                            </p>
                            <h2
                                style={{
                                    fontFamily: "'Playfair Display', Georgia, serif",
                                    fontSize: '32px',
                                    fontWeight: 400,
                                    color: '#1A1A1A',
                                    letterSpacing: '-0.3px',
                                }}
                            >
                                From the Collection
                            </h2>
                        </div>
                        <button
                            className="dhara-explore-link"
                            onClick={() => navigate('/template24/shop')}
                        >
                            Shop the Collection
                            <ArrowRight size={14} strokeWidth={1.5} />
                        </button>
                    </div>

                    {/* Product Grid — 4 items */}
                    <div
                        className="dhara-products-grid"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '32px',
                        }}
                    >
                        {products.map((product, index) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template24/product/${product.id}`)}
                                onMouseEnter={() => setHoveredProduct(index)}
                                onMouseLeave={() => setHoveredProduct(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                {/* Image */}
                                <div
                                    style={{
                                        overflow: 'hidden',
                                        aspectRatio: '4/5',
                                        backgroundColor: '#F0F0EE',
                                        marginBottom: '16px',
                                    }}
                                >
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        loading="lazy"
                                        className="dhara-product-img"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transform: hoveredProduct === index ? 'scale(1.04)' : 'scale(1)',
                                        }}
                                    />
                                </div>
                                {/* Name only — no price visible (editorial approach) */}
                                <h3
                                    style={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: '14px',
                                        fontWeight: 400,
                                        color: '#1A1A1A',
                                        letterSpacing: '0.3px',
                                        marginBottom: '4px',
                                    }}
                                >
                                    {product.name}
                                </h3>
                                <p
                                    style={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: '13px',
                                        fontWeight: 400,
                                        color: '#00553A',
                                        opacity: 0.8,
                                    }}
                                >
                                    {product.price}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: FULL-BLEED IMAGE BREAK ===== */}
            <section
                style={{
                    width: '100%',
                    height: '70vh',
                    minHeight: '400px',
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                <img
                    src="https://images.pexels.com/photos/13224226/pexels-photo-13224226.jpeg?auto=compress&cs=tinysrgb&w=1920"
                    alt="Fashion editorial"
                    loading="lazy"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
            </section>

            {/* ===== SECTION 6: JOURNAL PREVIEW ===== */}
            <section
                className="dhara-section-padding"
                style={{
                    padding: '100px 60px',
                    backgroundColor: '#FFFFFF',
                }}
            >
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    {/* Section Header */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            marginBottom: '56px',
                            flexWrap: 'wrap',
                            gap: '16px',
                        }}
                    >
                        <div>
                            <p
                                style={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    letterSpacing: '3px',
                                    textTransform: 'uppercase',
                                    color: '#00553A',
                                    marginBottom: '12px',
                                }}
                            >
                                From the Journal
                            </p>
                            <h2
                                style={{
                                    fontFamily: "'Playfair Display', Georgia, serif",
                                    fontSize: '32px',
                                    fontWeight: 400,
                                    color: '#1A1A1A',
                                    letterSpacing: '-0.3px',
                                }}
                            >
                                Stories & Craft
                            </h2>
                        </div>
                        <button
                            className="dhara-explore-link"
                            onClick={() => navigate('/template24/journal')}
                        >
                            All Stories
                            <ArrowRight size={14} strokeWidth={1.5} />
                        </button>
                    </div>

                    {/* Journal Grid — 2 articles */}
                    <div
                        className="dhara-journal-grid"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '48px',
                        }}
                    >
                        {journalArticles.map((article, index) => (
                            <article
                                key={article.id}
                                className="dhara-journal-card"
                                onClick={() => navigate('/template24/journal')}
                                onMouseEnter={() => setHoveredJournal(index)}
                                onMouseLeave={() => setHoveredJournal(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                {/* Article Image */}
                                <div
                                    style={{
                                        overflow: 'hidden',
                                        aspectRatio: '16/10',
                                        marginBottom: '24px',
                                        backgroundColor: '#FAFAF8',
                                    }}
                                >
                                    <img
                                        src={article.image}
                                        alt={article.title}
                                        loading="lazy"
                                        className="dhara-journal-img"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transition: 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                            transform: hoveredJournal === index ? 'scale(1.03)' : 'scale(1)',
                                        }}
                                    />
                                </div>
                                {/* Article Info */}
                                <p
                                    style={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: '11px',
                                        fontWeight: 400,
                                        letterSpacing: '2px',
                                        textTransform: 'uppercase',
                                        color: '#6B6B6B',
                                        marginBottom: '12px',
                                    }}
                                >
                                    {article.date}
                                </p>
                                <h3
                                    style={{
                                        fontFamily: "'Playfair Display', Georgia, serif",
                                        fontSize: '24px',
                                        fontWeight: 400,
                                        color: '#1A1A1A',
                                        lineHeight: 1.3,
                                        marginBottom: '12px',
                                        letterSpacing: '-0.3px',
                                        transition: 'color 0.3s ease',
                                        ...(hoveredJournal === index ? { color: '#00553A' } : {}),
                                    }}
                                >
                                    {article.title}
                                </h3>
                                <p
                                    style={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: '14px',
                                        fontWeight: 300,
                                        color: '#6B6B6B',
                                        lineHeight: 1.7,
                                        marginBottom: '20px',
                                    }}
                                >
                                    {article.excerpt}
                                </p>
                                <span
                                    className="dhara-explore-link"
                                    style={{
                                        fontSize: '11px',
                                        letterSpacing: '2px',
                                    }}
                                >
                                    Read More
                                    <ArrowRight size={12} strokeWidth={1.5} />
                                </span>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: NEWSLETTER — Ultra Clean ===== */}
            <section
                className="dhara-newsletter-section"
                style={{
                    padding: '100px 60px',
                    backgroundColor: '#FAFAF8',
                    textAlign: 'center',
                }}
            >
                <div style={{ maxWidth: '520px', margin: '0 auto' }}>
                    <h2
                        style={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                            fontSize: '18px',
                            fontWeight: 400,
                            fontStyle: 'italic',
                            color: '#00553A',
                            marginBottom: '12px',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Subscribe to our world
                    </h2>
                    <p
                        style={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: '14px',
                            fontWeight: 300,
                            color: '#6B6B6B',
                            lineHeight: 1.7,
                            marginBottom: '36px',
                        }}
                    >
                        Stories from the studio, early access to collections,
                        and quiet invitations to private events.
                    </p>

                    {subscribed ? (
                        <p
                            style={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '14px',
                                color: '#00553A',
                                padding: '16px 0',
                            }}
                        >
                            Thank you. Welcome to our world.
                        </p>
                    ) : (
                        <form
                            onSubmit={handleSubscribe}
                            style={{
                                display: 'flex',
                                maxWidth: '440px',
                                margin: '0 auto',
                            }}
                        >
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Your email address"
                                required
                                className="dhara-newsletter-input-main"
                                style={{
                                    flex: 1,
                                    padding: '14px 20px',
                                    border: '1px solid #E5E5E5',
                                    borderRight: 'none',
                                    backgroundColor: '#FFFFFF',
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: '13px',
                                    color: '#1A1A1A',
                                    transition: 'border-color 0.3s ease',
                                }}
                            />
                            <button
                                type="submit"
                                style={{
                                    backgroundColor: '#00553A',
                                    color: '#FFFFFF',
                                    border: '1px solid #00553A',
                                    padding: '14px 28px',
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    letterSpacing: '2px',
                                    textTransform: 'uppercase',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    whiteSpace: 'nowrap',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#006B4A')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#00553A')}
                            >
                                Subscribe
                                <ArrowRight size={14} strokeWidth={1.5} />
                            </button>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Template24;
